import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/ui/Header';
import ProfileCompletionMeter from './components/ProfileCompletionMeter';


import NotificationCenter from './components/NotificationCenter';

import QuickActionButtons from './components/QuickActionButtons';
import AnonymousProfilePreview from '../../components/ui/AnonymousProfilePreview';
import { useAuth } from '../../contexts/AuthContext';
import { hasActiveSubscription } from '../../utils/subscriptionUtils';
import { likesService, conversationsService, profileViewsService } from '../../services/supabaseService';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';



const MemberDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading } = useAuth();
  const [showProfilePreview, setShowProfilePreview] = useState(false);
  const [userGender, setUserGender] = useState('male');
  const [privacyLevel, setPrivacyLevel] = useState('high');
  const [dashboardData, setDashboardData] = useState({
    profileCompletion: 0,
    newMatches: [],
    recentMessages: [],
    profileViews: 0,
    likesReceived: 0,
    mutualMatches: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  // Profile completion from userProfile in render so it never shows 0% due to async delay/failure
  const profileCompletionFromProfile = useMemo(() => {
    if (!userProfile) return 0;
    const hasPhotos = Boolean(
      (Array.isArray(userProfile.photos) && userProfile.photos.length > 0) || userProfile.primaryPhoto
    );
    const hasBio = Boolean((userProfile.bio || '').trim());
    const hasInterests = Array.isArray(userProfile.interests) && userProfile.interests.length > 0;
    const hasLocation = Boolean((userProfile.city || '').trim());
    const completedCount = [hasPhotos, hasBio, hasInterests, hasLocation].filter(Boolean).length;
    return Math.round((completedCount / 4) * 100);
  }, [userProfile]);

  // Auth check is handled by ProtectedRoute
  useEffect(() => {
    if (user && userProfile) {
      loadDashboardData();
    }
  }, [user, userProfile]);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // Load user profile data
      setUserGender(userProfile?.gender || 'male');
      setPrivacyLevel(userProfile?.privacyLevel || 'high');

      // Load matches (mutual likes)
      const matches = await likesService?.getMutualMatches(user?.id);
      const matchProfiles = matches?.map(match => {
        const otherProfile = match?.likerId === user?.id ? match?.likedProfile : match?.likerProfile;
        return {
          id: otherProfile?.id,
          displayName: otherProfile?.displayName || 'Anonymous User',
          age: otherProfile?.age,
          location: otherProfile?.city,
          image: otherProfile?.primaryPhoto,
          imageAlt: otherProfile?.primaryPhotoAlt,
          compatibility: match?.compatibilityScore || 85,
          interests: otherProfile?.interests?.slice(0, 2) || [],
          isOnline: otherProfile?.isOnline,
          photoBlurred: otherProfile?.photoBlurred
        };
      });

      // Load profile views count
      const viewsCount = await profileViewsService?.getProfileViewsCount(user?.id);

      // Load likes received
      const likesReceived = await likesService?.getLikesReceived(user?.id);

      // Load recent conversations
      const conversations = await conversationsService?.getMyConversations(user?.id);
      const recentConversations = conversations?.slice(0, 4)?.map(conv => {
        const otherUser = conv?.participantOneId === user?.id ? conv?.participantTwo : conv?.participantOne;
        return {
          id: conv?.id,
          sender: otherUser?.displayName || 'Anonymous User',
          avatar: otherUser?.primaryPhoto,
          avatarAlt: otherUser?.primaryPhotoAlt,
          lastMessage: '',
          timestamp: new Date(conv?.lastMessageAt)?.toLocaleString(),
          unreadCount: 0,
          isOnline: otherUser?.isOnline
        };
      }) || [];

      // Compute profile completion from actual user data (not static)
      const hasPhotos = Boolean(
        (userProfile?.photos && userProfile.photos.length > 0) || userProfile?.primaryPhoto
      );
      const hasBio = Boolean(userProfile?.bio?.trim());
      const hasInterests = Array.isArray(userProfile?.interests) && userProfile.interests.length > 0;
      const hasLocation = Boolean(userProfile?.city?.trim());
      const completedCount = [hasPhotos, hasBio, hasInterests, hasLocation].filter(Boolean).length;
      const profileCompletion = Math.round((completedCount / 4) * 100);

      setDashboardData({
        profileCompletion,
        newMatches: matchProfiles || [],
        recentMessages: recentConversations,
        profileViews: viewsCount,
        likesReceived: likesReceived?.length || 0,
        mutualMatches: matches?.length || 0
      });
    } catch (error) {
      console.error('Load dashboard data error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditProfile = () => {
    navigate('/user-profile-settings');
  };

  const handleViewProfile = (profileId) => {
    navigate('/search-discovery', { state: { profileId } });
  };

  const handleViewAllMatches = () => {
    navigate('/search-discovery');
  };

  const handleOpenMessage = (conversationId) => {
    navigate('/messaging-system', { state: { conversationId } });
  };

  const handleViewAllMessages = () => {
    navigate('/messaging-system');
  };

  const handleViewAllNotifications = () => {
    navigate('/member-dashboard');
  };

  const handleSearchClick = () => {
    navigate('/search-discovery');
  };

  const handleMessagesClick = () => {
    navigate('/messaging-system');
  };

  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard - Bamchik Discreet Dating</title>
        <meta name="description" content="Your personal dashboard for managing matches, messages, and profile on Bamchik discreet dating platform" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="mx-auto max-w-[1200px] px-4 lg:px-6 py-6 md:py-8 lg:py-10">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold mb-2">
              Welcome Back, {userProfile?.displayName || 'User'}! 👋
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Here's what's happening with your profile today
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
            <div className="lg:col-span-2 space-y-4 md:space-y-6 lg:space-y-8">
              <ProfileCompletionMeter
                completionPercentage={profileCompletionFromProfile}
                onEditProfile={handleEditProfile}
                userProfile={userProfile}
              />

              {dashboardData?.newMatches?.length > 0 && (
                <div className="bg-card rounded-xl p-4 md:p-6 shadow-warm">
                  <div className="flex items-center justify-between mb-4 md:mb-6">
                    <div>
                      <h2 className="text-lg md:text-xl font-heading font-semibold mb-1">New Matches</h2>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        {dashboardData?.mutualMatches} mutual connection{dashboardData?.mutualMatches !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="ArrowRight"
                      iconPosition="right"
                      onClick={handleViewAllMatches}
                    >
                      View All
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {dashboardData?.newMatches?.slice(0, 4)?.map((match) => (
                      <div
                        key={match?.id}
                        onClick={() => handleViewProfile(match?.id)}
                        className="flex items-center gap-3 p-3 rounded-lg bg-muted hover:bg-muted/80 cursor-pointer transition-smooth"
                      >
                        <img
                          src={match?.image}
                          alt={match?.imageAlt}
                          className={`w-16 h-16 rounded-full object-cover ${match?.photoBlurred ? 'blur-md' : ''}`}
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium truncate">{match?.displayName}</h3>
                          <p className="text-sm text-muted-foreground">{match?.age}, {match?.location}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <Icon name="Heart" size={12} color="var(--color-success)" />
                            <span className="text-xs text-success font-bold">{match?.compatibility}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {dashboardData?.recentMessages?.length > 0 && (
                <div className="bg-card rounded-xl p-4 md:p-6 shadow-warm">
                  <div className="flex items-center justify-between mb-4 md:mb-6">
                    <div>
                      <h2 className="text-lg md:text-xl font-heading font-semibold mb-1">Recent Messages</h2>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        Stay connected with your matches
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="ArrowRight"
                      iconPosition="right"
                      onClick={handleViewAllMessages}
                    >
                      View All
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {dashboardData?.recentMessages?.map((msg) => (
                      <div
                        key={msg?.id}
                        onClick={() => handleOpenMessage(msg?.id)}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted cursor-pointer transition-smooth"
                      >
                        <img
                          src={msg?.avatar}
                          alt={msg?.avatarAlt}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium">{msg?.sender}</span>
                            <span className="text-xs text-muted-foreground">{msg?.timestamp}</span>
                          </div>
                          <p className="text-sm text-muted-foreground truncate">{msg?.lastMessage}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <QuickActionButtons
                onSearchClick={handleSearchClick}
                onMessagesClick={handleMessagesClick}
                onEditProfileClick={handleEditProfile}
              />
            </div>

            <div className="space-y-4 md:space-y-6 lg:space-y-8">
              <NotificationCenter onViewAll={handleViewAllNotifications} />

              <div className="bg-card rounded-xl p-4 md:p-6 shadow-warm">
                <div className="mb-4">
                  <h3 className="text-base md:text-lg font-heading font-semibold mb-1">
                    Activity Summary
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    Your profile interactions
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-3 md:gap-4">
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-xl md:text-2xl font-heading font-bold text-primary mb-1">
                      {dashboardData?.profileViews}
                    </div>
                    <div className="text-xs text-muted-foreground">Profile Views</div>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-xl md:text-2xl font-heading font-bold text-error mb-1">
                      {dashboardData?.likesReceived}
                    </div>
                    <div className="text-xs text-muted-foreground">Likes</div>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-xl md:text-2xl font-heading font-bold text-success mb-1">
                      {dashboardData?.mutualMatches}
                    </div>
                    <div className="text-xs text-muted-foreground">Matches</div>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl p-4 md:p-6 shadow-warm">
                <div className="mb-4">
                  <h3 className="text-base md:text-lg font-heading font-semibold mb-1">
                    Profile Preview
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    See how others view your profile
                  </p>
                </div>
                <button
                  onClick={() => setShowProfilePreview(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-primary to-secondary text-primary-foreground rounded-lg font-caption font-medium text-sm transition-smooth hover:opacity-90"
                >
                  <span>👁️</span>
                  Preview My Profile
                </button>
              </div>

              {userGender === 'male' && (
                <div className="bg-gradient-to-br from-success/10 to-success/5 rounded-xl p-4 md:p-6 border border-success/20">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="flex-shrink-0 text-2xl">👑</div>
                    <div>
                      <h3 className="text-base md:text-lg font-heading font-semibold mb-1">
                        {hasActiveSubscription(userProfile) ? 'Premium Active' : 'Upgrade to Premium'}
                      </h3>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        {hasActiveSubscription(userProfile) ?'Enjoying unlimited access to all features' :'Unlock unlimited messaging and features'
                        }
                      </p>
                    </div>
                  </div>
                  {hasActiveSubscription(userProfile) ? (
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-xs md:text-sm">
                        <span className="text-muted-foreground">Plan</span>
                        <span className="font-medium">{userProfile?.subscriptionPlan || 'Premium Monthly'}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs md:text-sm">
                        <span className="text-muted-foreground">Renews on</span>
                        <span className="font-medium font-mono">
                          {userProfile?.subscriptionRenewalDate 
                            ? new Date(userProfile?.subscriptionRenewalDate)?.toLocaleDateString()
                            : 'N/A'
                          }
                        </span>
                      </div>
                    </div>
                  ) : null}
                  <button
                    onClick={() => navigate('/subscription-payment')}
                    className="w-full px-4 py-2 bg-card text-foreground rounded-lg font-caption font-medium text-sm transition-smooth hover:bg-card/80"
                  >
                    {hasActiveSubscription(userProfile) ? 'Manage Subscription' : 'Upgrade Now'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </main>

        {showProfilePreview && (
          <AnonymousProfilePreview
            onClose={() => setShowProfilePreview(false)}
            onEditProfile={handleEditProfile}
            privacyLevel={privacyLevel}
            photoBlurred={userProfile?.photoBlurred}
            userProfile={userProfile}
          />
        )}
      </div>
    </>
  );
};

export default MemberDashboard;